import { generateClient } from 'aws-amplify/api'
import { env } from '../config/env'
import { formatUnknownError } from '../utils/format-error'
import type { CallChatMessage } from '../types/voice-call'
import type { ChatMessage } from '../types/chat'

/**
 * Call-scoped in-call chat. Messages typed in the call overlay 💬 panel are
 * persisted against the callId (CALL#{callId} / CHAT#…) as part of the call
 * record — NOT posted into any DM/group conversation thread — and delivered
 * live to every in-call participant via the onCallChatMessage(callId)
 * subscription (works for 1:1 and group calls alike).
 */

const client = generateClient()

const CALL_CHAT_FIELDS = /* GraphQL */ `
  callId
  messageId
  senderId
  senderName
  body
  createdAt
`

const sendCallChatMessageMutation = /* GraphQL */ `
  mutation SendCallChatMessage($input: SendCallChatMessageInput!) {
    sendCallChatMessage(input: $input) {
      ${CALL_CHAT_FIELDS}
    }
  }
`

const listCallChatMessagesQuery = /* GraphQL */ `
  query ListCallChatMessages($callId: ID!) {
    listCallChatMessages(callId: $callId) {
      ${CALL_CHAT_FIELDS}
    }
  }
`

const onCallChatMessageSubscription = /* GraphQL */ `
  subscription OnCallChatMessage($callId: ID!) {
    onCallChatMessage(callId: $callId) {
      ${CALL_CHAT_FIELDS}
    }
  }
`

/** Convert a server CallChatMessage to the shared ChatMessage UI shape. */
export function callChatToChatMessage(
  msg: CallChatMessage,
  currentUserId: string | undefined,
): ChatMessage {
  return {
    id: msg.messageId,
    threadId: msg.callId,
    text: msg.body,
    sender: currentUserId && msg.senderId === currentUserId ? 'me' : 'other',
    senderId: msg.senderId,
    senderName: msg.senderName,
    createdAt: Date.parse(msg.createdAt) || Date.now(),
    messageType: 'TEXT',
  }
}

export async function sendCallChatMessage(
  callId: string,
  body: string,
): Promise<CallChatMessage> {
  if (!env.appsyncGraphqlUrl) throw new Error('Chat is not available right now.')
  const trimmed = body.trim()
  if (!callId.trim() || !trimmed) throw new Error('Message body is required')
  const result = await client.graphql({
    query: sendCallChatMessageMutation,
    variables: { input: { callId, body: trimmed } },
    authMode: 'userPool',
  })
  if ('errors' in result && result.errors?.length) {
    throw new Error(result.errors.map((e) => formatUnknownError(e)).join('; '))
  }
  const msg = (result as { data?: { sendCallChatMessage?: CallChatMessage } }).data
    ?.sendCallChatMessage
  if (!msg) throw new Error('Empty call chat response')
  return msg
}

export async function listCallChatMessages(callId: string): Promise<CallChatMessage[]> {
  if (!env.appsyncGraphqlUrl || !callId.trim()) return []
  const result = await client.graphql({
    query: listCallChatMessagesQuery,
    variables: { callId },
    authMode: 'userPool',
  })
  if ('errors' in result && result.errors?.length) {
    throw new Error(result.errors.map((e) => formatUnknownError(e)).join('; '))
  }
  return (
    (result as { data?: { listCallChatMessages?: CallChatMessage[] } }).data
      ?.listCallChatMessages ?? []
  )
}

type CallChatSubscription = {
  subscribe: (observer: {
    next: (event: { data?: { onCallChatMessage?: CallChatMessage | null } }) => void
    error: (err: unknown) => void
  }) => { unsubscribe: () => void }
}

/**
 * Subscribe to live in-call chat for a call. Returns an unsubscribe handle.
 * Reconnects with capped backoff on transient subscription errors so a flaky
 * socket during a call self-heals without a refetch loop.
 */
export function subscribeToCallChat(
  callId: string,
  onMessage: (msg: CallChatMessage) => void,
): () => void {
  if (!env.appsyncGraphqlUrl || !callId.trim()) return () => {}

  let active = true
  let handle: { unsubscribe: () => void } | null = null
  let attempt = 0

  const connect = () => {
    if (!active) return
    const obs = client.graphql({
      query: onCallChatMessageSubscription,
      variables: { callId },
      authMode: 'userPool',
    }) as unknown as CallChatSubscription
    handle = obs.subscribe({
      next: (event) => {
        attempt = 0
        const msg = event.data?.onCallChatMessage
        if (msg) onMessage(msg)
      },
      error: (err) => {
        if (!active) return
        console.warn('[call-chat] subscription error — reconnecting', err)
        const delay = Math.min(15_000, 1000 * 2 ** Math.min(attempt, 4))
        attempt += 1
        setTimeout(() => {
          if (active) connect()
        }, delay)
      },
    })
  }

  connect()

  return () => {
    active = false
    try {
      handle?.unsubscribe()
    } catch {
      /* already closed */
    }
  }
}
